import React from 'react';
import { useTranslation } from 'localization';
import { Tooltip } from 'primereact/tooltip';
import { ChannelItem, ChannelItemType } from 'types/channel';
import { MarketplaceMetrics } from 'types/marketplace';
import ItemTypeGlyphAtom from '../../atoms/marketplace/ItemTypeGlyphAtom';
import TagAtom from '../../atoms/Tag/TagAtom';
import {
  MARKETPLACE_ITEM_TYPE_ICONS,
  MARKETPLACE_ITEM_TYPE_TONES,
} from '../../atoms/marketplace/iconMaps';
import { getMarketplaceItemType } from '../../../utils/marketplace/lookups';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import UseCaseTagPillsAtom from '../../atoms/marketplace/UseCaseTagPillsAtom';
import { getLocalizedText } from '../../../utils/getLocalizedText';

export interface ItemCardMoleculeProps {
  item: ChannelItem;
  metrics?: MarketplaceMetrics | null;
  onClick?: (item: ChannelItem) => void;
  onTagClick?: (tag: string) => void;
  onDeploy?: (item: ChannelItem) => void;
  deployLabel?: string;
  isDeploying?: boolean;
  applicabilityMatch?: boolean;
  hasEligibleSpheres?: boolean;
  /**
   * When provided, the deploy button renders in a disabled state and a
   * PrimeReact Tooltip (position="top") shows this message on hover.
   * Used to gate deploys on subscription status without hiding the button.
   */
  deployDisabledTooltip?: string;
  /** Name of the source channel when `item.is_syndicated` is true. Required to render the badge + attribution. */
  sourceChannelName?: string;
  /**
   * Optional deployment-mode hint. When `'once'`, the meta row renders a
   * "once" pill next to the version. Approach items may carry this; other
   * item types typically omit it.
   */
  deploymentMode?: 'once' | 'many';
  className?: string;
}

const ITEM_TYPE_KEYS: Record<ChannelItemType, string> = {
  approach: 'marketplace.common.itemType.approach',
  surface_template: 'marketplace.common.itemType.surfaceTemplate',
  assistant: 'marketplace.common.itemType.assistant',
  video: 'marketplace.common.itemType.video',
  form: 'marketplace.common.itemType.form',
  metaspace: 'marketplace.common.itemType.metaspace',
  schema: 'marketplace.common.itemType.schema',
  context: 'marketplace.common.itemType.context',
  arc_template: 'marketplace.common.itemType.arcTemplate',
  orbit_template: 'marketplace.common.itemType.orbitTemplate',
  aspect: 'marketplace.common.itemType.aspect',
  aspect_group: 'marketplace.common.itemType.aspectGroup',
  composition: 'marketplace.common.itemType.composition',
  archetype: 'marketplace.common.itemType.archetype',
};

const ItemCardMolecule: React.FC<ItemCardMoleculeProps> = ({
  item,
  metrics,
  onClick,
  onTagClick,
  onDeploy,
  deployLabel,
  isDeploying,
  applicabilityMatch,
  hasEligibleSpheres,
  deployDisabledTooltip,
  sourceChannelName,
  deploymentMode,
  className = '',
}) => {
  const { t } = useTranslation();
  const isSyndicated = Boolean(item.is_syndicated);
  const showSyndication = isSyndicated && Boolean(sourceChannelName);

  const reactId = React.useId();
  const deployTooltipTargetClass = `marketplace-item-card-molecule__deploy-btn--tooltip-${reactId.replaceAll(':', '')}`;

  const classes = [
    'marketplace-item-card-molecule',
    isSyndicated ? 'marketplace-item-card-molecule--syndicated' : '',
    onClick ? 'marketplace-item-card-molecule--clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const typeLabel = t(ITEM_TYPE_KEYS[item.item_type]);
  const displayName = getLocalizedText(item.item_metadata?.name) || typeLabel;
  const displayDescription = getLocalizedText(item.item_metadata?.description);
  const marketplaceType = getMarketplaceItemType(item.item_type);
  const showOncePill = deploymentMode === 'once';
  const isVersionable = item.item_version != null;

  const ratingAvg = metrics?.rating_average ?? 0;
  const importCount = metrics?.import_count ?? 0;

  const handleClick = () => {
    if (onClick) onClick(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(item);
    }
  };

  const useCaseTags = item.use_case_tags ?? [];

  return (
    <div
      className={classes}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
    >
      {showSyndication && (
        <div className="marketplace-item-card-molecule__badge-slot">
          <StatusBadgeAtom
            tone="info"
            icon="pi pi-link"
            label={t('marketplace.content.syndicatedBadge')}
            tooltip={t('marketplace.content.syndicatedFrom', {
              sourceChannelName: sourceChannelName as string,
            })}
            size="small"
          />
        </div>
      )}
      <div className="marketplace-item-card-molecule__header">
        <ItemTypeGlyphAtom
          type={marketplaceType}
          size={38}
          ariaLabel={typeLabel}
          className="marketplace-item-card-molecule__glyph"
        />
        <div className="marketplace-item-card-molecule__title-area">
          <h4 className="marketplace-item-card-molecule__name">{displayName}</h4>
          {showSyndication && (
            <p className="marketplace-item-card-molecule__attribution">
              {t('marketplace.content.syndicatedFrom', {
                sourceChannelName: sourceChannelName as string,
              })}
            </p>
          )}
          <div className="marketplace-item-card-molecule__meta">
            <TagAtom
              tone={MARKETPLACE_ITEM_TYPE_TONES[marketplaceType]}
              icon={MARKETPLACE_ITEM_TYPE_ICONS[marketplaceType]}
              label={typeLabel}
              size="small"
              className="marketplace-item-card-molecule__type-chip"
            />
            {isVersionable && (
              <>
                <span className="marketplace-item-card-molecule__separator" aria-hidden="true">
                  &middot;
                </span>
                <span className="marketplace-item-card-molecule__version">
                  {t('marketplace.common.version', { version: item.item_version })}
                </span>
              </>
            )}
            {showOncePill && (
              <>
                <span className="marketplace-item-card-molecule__separator" aria-hidden="true">
                  &middot;
                </span>
                <span
                  className="marketplace-item-card-molecule__once-pill"
                  aria-label={t('marketplace.channelDetail.itemCard.deployOnce')}
                >
                  <i
                    className="pi pi-lock marketplace-item-card-molecule__once-pill-icon"
                    aria-hidden="true"
                  />
                  {t('marketplace.channelDetail.itemCard.deployOnce')}
                </span>
              </>
            )}
          </div>
        </div>
        {onClick && (
          <i
            className="pi pi-chevron-right marketplace-item-card-molecule__chevron"
            aria-hidden="true"
          />
        )}
      </div>

      {displayDescription && (
        <p className="marketplace-item-card-molecule__description">{displayDescription}</p>
      )}

      {ratingAvg > 0 && (
        <div className="marketplace-item-card-molecule__rating">
          <StarDisplayAtom rating={ratingAvg} size="small" />
          {importCount > 0 && (
            <>
              <span className="marketplace-item-card-molecule__separator">&middot;</span>
              <span className="marketplace-item-card-molecule__import-count">
                {t('marketplace.common.importCount', { count: importCount })}
              </span>
            </>
          )}
        </div>
      )}

      {!ratingAvg && importCount > 0 && (
        <div className="marketplace-item-card-molecule__rating">
          <span className="marketplace-item-card-molecule__import-count">
            {t('marketplace.common.importCount', { count: importCount })}
          </span>
        </div>
      )}

      {useCaseTags.length > 0 && (
        <UseCaseTagPillsAtom
          tags={useCaseTags}
          maxVisible={3}
          size="small"
          onTagClick={onTagClick}
          className="marketplace-item-card-molecule__tags"
        />
      )}

      {onDeploy && (
        <div className="marketplace-item-card-molecule__deploy-area">
          {(() => {
            const isNoMatch = applicabilityMatch === false;
            const hasTargets = hasEligibleSpheres !== false;
            const isDisabledByApplicability = isNoMatch && !hasTargets;
            const gateDisabled = Boolean(deployDisabledTooltip);
            const isDisabled = isDeploying || isDisabledByApplicability || gateDisabled;

            let label: string;
            if (isDeploying) {
              label = t('marketplace.deployWizard.actions.deploying');
            } else if (isNoMatch && hasTargets) {
              label = t('marketplace.deploy.deployToButton');
            } else {
              label = deployLabel || t('marketplace.deploy.deployButton');
            }

            const btnClass = [
              'marketplace-item-card-molecule__deploy-btn',
              isDisabledByApplicability
                ? 'marketplace-item-card-molecule__deploy-btn--no-eligible'
                : '',
              gateDisabled ? deployTooltipTargetClass : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <>
                <button
                  type="button"
                  className={btnClass}
                  disabled={isDisabled}
                  title={
                    isDisabledByApplicability
                      ? t('marketplace.deploy.noEligibleSpheres')
                      : undefined
                  }
                  data-pr-tooltip={gateDisabled ? deployDisabledTooltip : undefined}
                  data-pr-position={gateDisabled ? 'top' : undefined}
                  aria-label={gateDisabled ? deployDisabledTooltip : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) onDeploy(item);
                  }}
                >
                  {label}
                </button>
                {gateDisabled && <Tooltip target={`.${deployTooltipTargetClass}`} />}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ItemCardMolecule;
